/**
 * One place that turns an API timestamp into something a person reads.
 *
 * The API answers ISO 8601 with an offset — `2026-08-22T18:58:08+00:00` —
 * and a table cell rendering that verbatim is what the case list shipped
 * with. It is precise, unambiguous, and nobody reads it.
 *
 * ## Formatted in the UI's locale, not the browser's
 *
 * The caller passes `i18n.language`, exactly as `SessionList` already does
 * for `last_activity`. A page the user has switched to Arabic renders its
 * dates in Arabic; a browser configured for another locale entirely does
 * not override the language the page is being read in.
 *
 * ## An unparseable value comes back unchanged
 *
 * `new Date('nonsense')` is an Invalid Date, and `Intl.DateTimeFormat`
 * renders that as the literal string "Invalid Date" — a phrase in one
 * language, in a UI that has two, standing where a date should be. Handing
 * back the raw value instead means the screen shows what the API actually
 * said, which is both honest and diagnosable.
 */
export function formatTimestamp(value: string, locale: string): string {
  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    parsed,
  );
}

/**
 * "5 minutes ago", "yesterday", "in 3 days" — how long ago a timestamp was,
 * in the reader's language, for the audit trail's time column where the
 * question is usually "how recent is this" before it is "which minute".
 *
 * `numeric: 'auto'` so the language's own words are used where it has them
 * ("yesterday", not "1 day ago"). Anything over ~30 days falls back to the
 * absolute form, because "11 months ago" is less useful than a date.
 * An unparseable value comes back unchanged, for `formatTimestamp`'s reason.
 */
export function formatRelative(value: string, locale: string, now: Date = new Date()): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  const seconds = Math.round((parsed.getTime() - now.getTime()) / 1000);
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const abs = Math.abs(seconds);

  if (abs < 45) return format.format(0, 'second');
  if (abs < 45 * 60) return format.format(Math.round(seconds / 60), 'minute');
  if (abs < 22 * 3600) return format.format(Math.round(seconds / 3600), 'hour');
  if (abs < 30 * 86400) return format.format(Math.round(seconds / 86400), 'day');
  return formatTimestamp(value, locale);
}
